import React, { useState, useEffect } from "react";
import { Spin } from "antd";
import { XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart, CartesianGrid } from "recharts";
import moment from "moment";
import { useDispatch } from "react-redux";
import { getCoursePriceHistory, getRelatedCourses } from "../../../appRedux/actions/courses";

const TIME_RANGE_MAP = { "30 Days": 30, "90 Days": 90, "1 Year": 180, "All Time": "all" };

const DEAL_RATING_STYLES = {
  Great: { bg: "#fff0e6", text: "#fc7728" },
  Good: { bg: "#e6f9e6", text: "#2e7d32" },
  Normal: { bg: "#e6f0ff", text: "#1565c0" },
  Poor: { bg: "#fde6e6", text: "#c62828" },
};

const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };

const PriceHistoryModal = ({ visible, onClose, courseId, currencyInfo, courseName = "Advanced Web Development Bootcamp 2024" }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [apiStats, setApiStats] = useState({});
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [timeRange, setTimeRange] = useState("90 Days");
  const [viewType, setViewType] = useState("area");
  const [showSimilar, setShowSimilar] = useState(false);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedFetched, setRelatedFetched] = useState(false);

  // Reset related courses state on fresh modal open
  useEffect(() => {
    if (visible && courseId) {
      setRelatedCourses([]);
      setShowSimilar(false);
      setRelatedFetched(false);
    }
  }, [visible, courseId]);

  // Fetch price history when modal is visible and courseId/timeRange changes
  useEffect(() => {
    if (visible && courseId) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const days = TIME_RANGE_MAP[timeRange];
          const result = await dispatch(getCoursePriceHistory(courseId, days));
          if (result) {
            const formatted = (result.history || []).map((item) => ({
              ...item,
              formattedDate: moment(item.recorded_at).format("MMM DD"),
            }));
            setHistoryData(formatted);
            setApiStats(result.stats || {});
          } else {
            setHistoryData([]);
            setApiStats({});
          }
        } catch (error) {
          console.error("Failed to load price history:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [visible, courseId, timeRange, dispatch]);

  // Fetch related courses on first accordion expand
  useEffect(() => {
    if (showSimilar && courseId && !relatedFetched) {
      const fetchRelated = async () => {
        setRelatedLoading(true);
        try {
          const courses = await dispatch(getRelatedCourses(courseId));
          setRelatedCourses(courses || []);
        } catch (error) {
          console.error("Failed to load related courses:", error);
        } finally {
          setRelatedLoading(false);
          setRelatedFetched(true);
        }
      };
      fetchRelated();
    }
  }, [showSimilar, courseId, relatedFetched, dispatch]);

  if (!visible) return null;

  // --- Derived display values ---
  const rawCurrency = historyData[0]?.currency || "INR";
  const currency = CURRENCY_SYMBOLS[rawCurrency] || rawCurrency;

  const { current_price, lowest_price, highest_price, average_price, popularity_score, deal_rating, period_days } = apiStats;
  const priceDiff = average_price && current_price ? average_price - current_price : 0;
  const discountPct = average_price > 0 ? Math.abs(priceDiff / average_price * 100).toFixed(1) : null;
  const isBelowAvg = priceDiff > 0;
  const ratingStyle = DEAL_RATING_STYLES[deal_rating] || DEAL_RATING_STYLES["Normal"];
  const chartTitle = timeRange === "All Time" ? "Price History (All Time)" : `Price History (${period_days || TIME_RANGE_MAP[timeRange]} Days)`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const p = payload[0].payload;
      const sym = CURRENCY_SYMBOLS[p.currency || rawCurrency] || rawCurrency;
      return (
        <div className="tw-bg-white tw-p-3 tw-border tw-border-solid tw-border-[#bfc8cc] tw-rounded-lg tw-shadow-md">
          <p className="tw-font-['Inter'] tw-text-[13px] tw-font-semibold tw-text-[#191c1d] tw-mb-1">{label}</p>
          <p className="tw-font-['Inter'] tw-text-[13px] tw-text-[#006174] tw-font-bold tw-m-0">Price: {sym}{payload[0].value}</p>
          {p.list_price && <p className="tw-font-['Inter'] tw-text-[13px] tw-text-[#3f484b] tw-m-0 tw-mt-1">List: {sym}{p.list_price}</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-[#191c1d]/40 tw-backdrop-blur-[2px] tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-6">
      <div className="tw-bg-[#f3f4f5] tw-w-full tw-max-w-[1100px] tw-max-h-[90vh] tw-rounded-xl tw-shadow-2xl tw-flex tw-flex-col tw-overflow-hidden tw-ring-1 tw-ring-[#bfc8cc]/50">

        {/* Header */}
        <header className="tw-flex tw-justify-between tw-items-center tw-px-6 tw-py-4 tw-bg-white tw-border-b tw-border-solid tw-border-[#e1e3e4] tw-z-10 tw-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-[#e9f9ff] tw-flex tw-items-center tw-justify-center tw-text-[#006174]">
              <span className="material-symbols-outlined tw-text-[24px]">analytics</span>
            </div>
            <div>
              <h2 className="tw-font-['Work_Sans'] tw-text-[20px] tw-font-bold tw-text-[#191c1d] tw-m-0 tw-leading-tight">Price History</h2>
              <p className="tw-font-['Inter'] tw-text-[14px] tw-text-[#3f484b] tw-m-0">{courseName}</p>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4">
            <button className="tw-flex tw-items-center tw-gap-1.5 tw-text-[#006174] hover:tw-text-[#0b677b] tw-bg-transparent tw-border-none tw-cursor-pointer tw-font-['Inter'] tw-font-semibold tw-text-[14px] tw-transition-colors">
              <span className="material-symbols-outlined tw-text-[18px]">query_stats</span>
              View Full Price Analytics
            </button>
            <button onClick={onClose} className="tw-w-8 tw-h-8 tw-rounded-md tw-bg-transparent hover:tw-bg-[#f3f4f5] tw-border-none tw-flex tw-items-center tw-justify-center tw-text-[#3f484b] tw-transition-colors tw-cursor-pointer">
              <span className="material-symbols-outlined tw-text-[22px]">close</span>
            </button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-5 tw-relative">
          {loading && (
            <div className="tw-absolute tw-inset-0 tw-bg-white/50 tw-backdrop-blur-sm tw-z-20 tw-flex tw-items-center tw-justify-center"><Spin size="large" /></div>
          )}

          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[1fr_270px] tw-gap-4">
            
            {/* Chart Container */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-[#e1e3e4] tw-shadow-sm tw-p-5 tw-flex tw-flex-col tw-relative">
              <div className="tw-flex tw-justify-between tw-items-start tw-mb-6 tw-z-10">
                <div>
                  <h3 className="tw-font-['Inter'] tw-text-[16px] tw-font-bold tw-text-[#191c1d] tw-m-0">{chartTitle}</h3>
                  <p className="tw-font-['Inter'] tw-text-[13px] tw-text-[#3f484b] tw-m-0 tw-mt-1">Tracking global marketplace fluctuations.</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                  <div className="tw-relative">
                    <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="tw-appearance-none tw-bg-white tw-border tw-border-solid tw-border-[#bfc8cc] tw-rounded-md tw-pl-3 tw-pr-8 tw-py-1.5 tw-font-['Inter'] tw-text-[13px] tw-font-medium tw-text-[#191c1d] tw-cursor-pointer focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-[#006174]">
                      {Object.keys(TIME_RANGE_MAP).map((key) => (<option key={key}>{key}</option>))}
                    </select>
                    <span className="material-symbols-outlined tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-[18px] tw-pointer-events-none tw-text-[#3f484b]">expand_more</span>
                  </div>
                  <div className="tw-flex tw-border tw-border-solid tw-border-[#bfc8cc] tw-rounded-md tw-overflow-hidden">
                    <button onClick={() => setViewType("line")} className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-border-r tw-border-solid tw-border-[#bfc8cc] tw-transition-colors ${viewType === 'line' ? 'tw-bg-[#e9f9ff] tw-text-[#006174]' : 'tw-bg-white tw-text-[#3f484b] hover:tw-bg-[#f3f4f5]'}`}>
                      <span className="material-symbols-outlined tw-text-[18px]">show_chart</span>
                    </button>
                    <button onClick={() => setViewType("area")} className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-border-r tw-border-solid tw-border-[#bfc8cc] tw-transition-colors ${viewType === 'area' ? 'tw-bg-[#e9f9ff] tw-text-[#006174] icon-fill' : 'tw-bg-white tw-text-[#3f484b] hover:tw-bg-[#f3f4f5]'}`}>
                      <span className="material-symbols-outlined tw-text-[18px]">area_chart</span>
                    </button>
                    <button onClick={() => setViewType("bar")} className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-transition-colors ${viewType === 'bar' ? 'tw-bg-[#e9f9ff] tw-text-[#006174]' : 'tw-bg-white tw-text-[#3f484b] hover:tw-bg-[#f3f4f5]'}`}>
                      <span className="material-symbols-outlined tw-text-[18px]">bar_chart</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="tw-flex-1 tw-min-h-[250px] tw-relative tw-w-full -tw-ml-2">
                {historyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historyData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#006174" stopOpacity={0.15}/>
                          <stop offset="100%" stopColor="#006174" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#f0f1f2" strokeDasharray="4 4" />
                      <XAxis dataKey="formattedDate" tick={{ fontSize: 10, fill: "#6f797c", fontFamily: "Inter" }} tickLine={false} axisLine={false} dy={10}/>
                      <YAxis tick={{ fontSize: 10, fill: "#6f797c", fontFamily: "Inter" }} tickLine={false} axisLine={false} tickFormatter={(val) => `${currency}${val}`} width={55}/>
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="price" stroke="#006174" strokeWidth={3} fillOpacity={1} fill="url(#chartGradient)" animationDuration={1500} dot={historyData.length === 1 ? { r: 4, fill: "#fc7728", stroke: "#fff", strokeWidth: 2 } : { r: 0 }} activeDot={{ r: 6, fill: "#fc7728", stroke: "#ffffff", strokeWidth: 2 }}/>
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-[#3f484b] tw-font-['Inter'] tw-text-[14px]">No price history data available.</div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="tw-flex tw-flex-col tw-gap-4">
              
              {/* Current Price Box */}
              <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-[#e1e3e4] tw-border-t-4 tw-border-t-[#fc7728] tw-p-5 tw-flex tw-flex-col">
                <p className="tw-font-['Inter'] tw-text-[12px] tw-text-[#3f484b] tw-uppercase tw-tracking-wider tw-font-semibold tw-m-0 tw-mb-2">CURRENT PRICE</p>
                <div className="tw-flex tw-items-end tw-gap-3 tw-mb-2">
                  <h1 className="tw-font-['Work_Sans'] tw-text-[32px] tw-font-bold tw-text-[#191c1d] tw-m-0 tw-leading-none">
                    {current_price != null ? `${currency}${current_price}` : "—"}
                  </h1>
                  {discountPct && (
                    <span className={`tw-font-['Inter'] tw-text-[13px] tw-font-bold tw-flex tw-items-center tw-mb-1 ${isBelowAvg ? 'tw-text-[#2e7d32]' : 'tw-text-[#c62828]'}`}>
                      <span className="material-symbols-outlined tw-text-[18px] tw-font-bold">{isBelowAvg ? 'arrow_downward' : 'arrow_upward'}</span> {discountPct}%
                    </span>
                  )}
                </div>
                {average_price != null && (
                  <p className="tw-font-['Inter'] tw-text-[12px] tw-text-[#6f797c] tw-m-0">
                    Compared to {timeRange === "All Time" ? "all-time" : `${period_days || TIME_RANGE_MAP[timeRange]}-day`} average of {currency}{average_price.toFixed(2)}.
                  </p>
                )}
                
                <hr className="tw-w-full tw-m-0 tw-my-4 tw-border-0 tw-border-t tw-border-solid tw-border-[#e1e3e4]" />
                
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="material-symbols-outlined tw-text-[#fc7728] tw-text-[20px] icon-fill">notifications_active</span>
                    <span className="tw-font-['Inter'] tw-text-[14px] tw-font-bold tw-text-[#191c1d]">Price Drop Alert</span>
                  </div>
                  <button onClick={() => setAlertEnabled(!alertEnabled)} className={`tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full tw-transition-colors tw-cursor-pointer tw-border-none ${alertEnabled ? 'tw-bg-[#fc7728]' : 'tw-bg-[#e1e3e4]'}`}>
                    <span className={`tw-inline-block tw-h-5 tw-w-5 tw-transform tw-rounded-full tw-bg-white tw-transition-transform tw-shadow-sm ${alertEnabled ? 'tw-translate-x-5' : 'tw-translate-x-0.5'}`}></span>
                  </button>
                </div>
                
                <button className="tw-w-full tw-py-2.5 tw-rounded-md tw-bg-[#fc7728] tw-text-white tw-border-none tw-font-['Inter'] tw-text-[14px] tw-font-bold tw-shadow-sm hover:tw-opacity-90 tw-transition-all tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-2">
                  <span className="material-symbols-outlined tw-text-[20px]">shopping_cart_checkout</span>
                  Purchase Deal Now
                </button>
              </div>

              {/* Market Position Box */}
              <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-[#e1e3e4] tw-border-t-4 tw-border-t-[#006174] tw-p-5 tw-flex tw-flex-col">
                <p className="tw-font-['Inter'] tw-text-[12px] tw-text-[#3f484b] tw-uppercase tw-tracking-wider tw-font-semibold tw-m-0 tw-mb-4">MARKET POSITION</p>
                <div className="tw-flex tw-flex-col tw-gap-4">
                  <div className="tw-flex tw-justify-between tw-items-center">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="material-symbols-outlined tw-text-[18px] tw-text-[#2e7d32]">trending_down</span>
                      <span className="tw-font-['Inter'] tw-text-[13px] tw-text-[#3f484b]">Lowest (All Time)</span>
                    </div>
                    <span className="tw-font-['Inter'] tw-text-[14px] tw-font-bold tw-text-[#191c1d]">{lowest_price != null ? `${currency}${lowest_price}` : "—"}</span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-center">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="material-symbols-outlined tw-text-[18px] tw-text-[#2e7d32]">trending_up</span>
                      <span className="tw-font-['Inter'] tw-text-[13px] tw-text-[#3f484b]">Highest (All Time)</span>
                    </div>
                    <span className="tw-font-['Inter'] tw-text-[14px] tw-font-bold tw-text-[#191c1d]">{highest_price != null ? `${currency}${highest_price}` : "—"}</span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-center">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="material-symbols-outlined tw-text-[18px] tw-text-[#2e7d32]">bar_chart</span>
                      <span className="tw-font-['Inter'] tw-text-[13px] tw-text-[#3f484b]">Popularity Score</span>
                    </div>
                    <span className="tw-font-['Inter'] tw-text-[14px] tw-font-bold tw-text-[#191c1d]">{popularity_score != null ? `${popularity_score}/10` : "—"}</span>
                  </div>
                  
                  <hr className="tw-w-full tw-m-0 tw-border-0 tw-border-t tw-border-solid tw-border-[#e1e3e4]" />
                  
                  <div className="tw-flex tw-justify-between tw-items-center">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="material-symbols-outlined tw-text-[18px] tw-text-[#2e7d32] icon-fill">verified</span>
                      <span className="tw-font-['Inter'] tw-text-[13px] tw-font-bold tw-text-[#191c1d]">Deal Rating</span>
                    </div>
                    <span className="tw-px-2 tw-py-1 tw-rounded tw-font-['Inter'] tw-font-bold tw-text-[11px] tw-uppercase" style={{ backgroundColor: ratingStyle.bg, color: ratingStyle.text }}>
                      {deal_rating || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similarly Priced Courses */}
          <div className="tw-mt-6 tw-bg-[#f8f9fa] tw-rounded-lg tw-border tw-border-solid tw-border-[#e1e3e4] tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col">
            <button onClick={() => setShowSimilar(!showSimilar)} className="tw-w-full tw-flex tw-justify-between tw-items-center tw-px-5 tw-py-4 tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-[#e7e8e9] tw-transition-colors">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-8 tw-h-8 tw-rounded-md tw-bg-[#e1e3e4] tw-flex tw-items-center tw-justify-center tw-text-[#191c1d]">
                  <span className="material-symbols-outlined tw-text-[18px]">compare</span>
                </div>
                <span className="tw-font-['Inter'] tw-text-[15px] tw-font-bold tw-text-[#191c1d]">View Similarly Priced Courses</span>
                {relatedFetched && (
                  <span className="tw-bg-[#e1e3e4] tw-text-[#3f484b] tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-bold">
                    {relatedCourses.length} Found
                  </span>
                )}
              </div>
              <span className={`material-symbols-outlined tw-text-[#3f484b] tw-transition-transform tw-duration-300 ${showSimilar ? 'tw-rotate-180' : ''}`}>expand_more</span>
            </button>
            
            {showSimilar && (
              <div className="tw-p-5 tw-pt-0">
                {relatedLoading ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-py-8"><Spin /></div>
                ) : relatedCourses.length > 0 ? (
                  <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
                    {relatedCourses.map((course) => (
                      <a key={course._id} href={course.affiliate_url || "#"} target="_blank" rel="noopener noreferrer" className="tw-no-underline tw-group">
                        <div className="tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-[#e1e3e4] tw-overflow-hidden group-hover:tw-shadow-md tw-transition-shadow">
                          <div className="tw-h-[120px] tw-w-full tw-bg-[#f3f4f5] tw-relative">
                            {course.image_url ? (
                              <img alt={course.title} className="tw-w-full tw-h-full tw-object-cover" src={course.image_url} />
                            ) : (
                              <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-[#bfc8cc]">
                                <span className="material-symbols-outlined tw-text-[32px]">image</span>
                              </div>
                            )}
                          </div>
                          <div className="tw-p-3">
                            <p className="tw-font-['Inter'] tw-text-[13px] tw-font-bold tw-text-[#191c1d] tw-m-0 tw-line-clamp-2">{course.title}</p>
                            <div className="tw-flex tw-items-center tw-justify-between tw-mt-2">
                              <span className="tw-font-['Inter'] tw-text-[13px] tw-font-bold tw-text-[#006174]">
                                {course.price != null ? `${currency}${course.price}` : "—"}
                              </span>
                              {course.rating != null && (
                                <span className="tw-font-['Inter'] tw-text-[11px] tw-text-[#6f797c] tw-flex tw-items-center tw-gap-0.5">
                                  <span className="material-symbols-outlined tw-text-[14px] tw-text-[#f59e0b] icon-fill">star</span>
                                  {course.rating}
                                </span>
                              )}
                            </div>
                            {course.merchant && (
                              <span className="tw-inline-block tw-mt-1.5 tw-bg-[#f3f4f5] tw-text-[#3f484b] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-['Inter'] tw-text-[10px] tw-font-medium tw-capitalize">
                                {course.merchant}
                              </span>
                            )}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-center tw-py-8 tw-text-[#6f797c] tw-font-['Inter'] tw-text-[14px]">
                    No similarly priced courses found.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryModal;
